// FILE: Sidebar.focus.logic.ts
// Purpose: Pure data selection for the project-focused sidebar and attention indicator.
// Exports: Focused thread, recent archive, and cross-project attention selectors.

import type { ThreadId } from "@taskin-code/contracts";
import {
  isThreadSnoozed,
  threadHasUnseenCompletion,
  threadNeedsAttention,
} from "../lib/threadInboxState";
import { isCrossProjectDelegate, isInitiativeChildThread } from "@taskin-code/shared/delegation";
import type { Project, SidebarThreadSummary, Thread } from "../types";

type FocusThread = Pick<SidebarThreadSummary, "id" | "projectId" | "title" | "createdAt"> & {
  kind?: "standard" | "initiative";
  parentThreadId?: ThreadId | null;
  subagentRole?: string | null;
  updatedAt?: SidebarThreadSummary["updatedAt"];
  latestUserMessageAt?: SidebarThreadSummary["latestUserMessageAt"];
  archivedAt?: SidebarThreadSummary["archivedAt"];
  deletedAt?: string | null;
};

export interface RelatedProjectThread<T extends FocusThread = FocusThread> {
  thread: T;
  relatedTo: {
    thread: T;
    relation: "child-of" | "parent-of";
  };
}

export interface RelatedProjectThreadGroup<T extends FocusThread = FocusThread> {
  project: Project;
  rows: Array<RelatedProjectThread<T>>;
}

export type CrossProjectAttentionKind = "approval" | "input" | "plan" | "completed";

export interface CrossProjectAttention {
  level: "urgent" | "info" | null;
  total: number;
  byProject: Array<{
    project: Project;
    threads: Array<{ thread: FocusThread; kind: CrossProjectAttentionKind }>;
  }>;
}

/**
 * Keep initiatives in the focused-sidebar section. Initiative children are
 * ordinary first-level threads and belong in the regular "Hilos" group.
 */
export function partitionFocusedInitiatives<
  T extends { id: ThreadId; parentThreadId?: ThreadId | null; kind?: "standard" | "initiative" },
>(threads: readonly T[]): { initiatives: T[]; rest: T[] } {
  return {
    initiatives: threads.filter((thread) => thread.kind === "initiative"),
    rest: threads.filter((thread) => thread.kind !== "initiative"),
  };
}

/** The focused archive section deliberately has no child/delegation rows. */
export function recentlyArchivedThreads<T extends FocusThread>(
  threads: readonly T[],
  projectId: Thread["projectId"],
  limit = 5,
): T[] {
  const threadById = new Map(threads.map((thread) => [thread.id, thread] as const));
  return threads
    .filter(
      (thread) =>
        thread.projectId === projectId &&
        thread.archivedAt != null &&
        (thread.parentThreadId == null ||
          isInitiativeChildThread(thread, (id) => threadById.get(id)) ||
          isCrossProjectDelegate(thread, (id) => threadById.get(id))),
    )
    .toSorted((left, right) => {
      const rightArchivedAt = Date.parse(right.archivedAt!);
      const leftArchivedAt = Date.parse(left.archivedAt!);
      const byArchivedAt = rightArchivedAt - leftArchivedAt;
      return byArchivedAt === 0 ? right.id.localeCompare(left.id) : byArchivedAt;
    })
    .slice(0, limit);
}

/**
 * Cross-project delegation rows for the focused sidebar. A row is emitted for
 * every focused-thread relationship, so an external parent delegated to more
 * than one focused thread remains explainable instead of arbitrarily choosing
 * one child. Rows are grouped by their external project.
 */
export function selectRelatedProjectThreads<T extends FocusThread>(input: {
  threads: readonly T[];
  projects: readonly Project[];
  focusedProjectId: Thread["projectId"];
}): Array<RelatedProjectThreadGroup<T>> {
  const focusedProject = input.projects.find((project) => project.id === input.focusedProjectId);
  if (!focusedProject) return [];

  const visibleThreads = input.threads.filter(
    (thread) => thread.archivedAt == null && thread.deletedAt == null,
  );
  const focusedThreads = visibleThreads.filter(
    (thread) => thread.projectId === input.focusedProjectId,
  );
  const focusedThreadById = new Map(focusedThreads.map((thread) => [thread.id, thread] as const));
  const focusedChildrenByParentId = new Map<ThreadId, T[]>();

  for (const focusedThread of focusedThreads) {
    const parentThreadId = focusedThread.parentThreadId;
    if (!parentThreadId) continue;
    const children = focusedChildrenByParentId.get(parentThreadId) ?? [];
    children.push(focusedThread);
    focusedChildrenByParentId.set(parentThreadId, children);
  }

  const rowsByProjectId = new Map<Project["id"], Array<RelatedProjectThread<T>>>();
  for (const thread of visibleThreads) {
    if (thread.projectId === input.focusedProjectId) continue;

    const relatedRows: Array<RelatedProjectThread<T>> = [];
    const focusedParent = thread.parentThreadId
      ? focusedThreadById.get(thread.parentThreadId)
      : undefined;
    if (focusedParent) {
      relatedRows.push({
        thread,
        relatedTo: { thread: focusedParent, relation: "child-of" },
      });
    }
    for (const focusedChild of focusedChildrenByParentId.get(thread.id) ?? []) {
      relatedRows.push({
        thread,
        relatedTo: { thread: focusedChild, relation: "parent-of" },
      });
    }
    if (relatedRows.length === 0) continue;

    const rows = rowsByProjectId.get(thread.projectId) ?? [];
    rows.push(...relatedRows);
    rowsByProjectId.set(thread.projectId, rows);
  }

  const projectById = new Map(input.projects.map((project) => [project.id, project] as const));
  const linkedProjectIds = new Set(focusedProject.linkedProjectIds);
  const orderedProjectIds = [...rowsByProjectId.keys()]
    .filter((projectId) => projectById.has(projectId))
    .toSorted((left, right) => {
      const leftLinkedIndex = focusedProject.linkedProjectIds.indexOf(left);
      const rightLinkedIndex = focusedProject.linkedProjectIds.indexOf(right);
      const leftIsLinked = linkedProjectIds.has(left);
      const rightIsLinked = linkedProjectIds.has(right);
      if (leftIsLinked !== rightIsLinked) return leftIsLinked ? -1 : 1;
      if (leftIsLinked && rightIsLinked) return leftLinkedIndex - rightLinkedIndex;
      return projectById.get(left)!.name.localeCompare(projectById.get(right)!.name);
    });

  return orderedProjectIds.map((projectId) => ({
    project: projectById.get(projectId)!,
    rows: rowsByProjectId.get(projectId)!.toSorted((left, right) => {
      const leftUpdatedAt = Date.parse(left.thread.updatedAt ?? left.thread.createdAt);
      const rightUpdatedAt = Date.parse(right.thread.updatedAt ?? right.thread.createdAt);
      const byUpdatedAt = rightUpdatedAt - leftUpdatedAt;
      if (byUpdatedAt !== 0) return byUpdatedAt;
      const byThreadId = right.thread.id.localeCompare(left.thread.id);
      if (byThreadId !== 0) return byThreadId;
      return left.relatedTo.thread.id.localeCompare(right.relatedTo.thread.id);
    }),
  }));
}

function attentionKind(summary: SidebarThreadSummary): CrossProjectAttentionKind | null {
  if (!threadNeedsAttention(summary)) {
    return threadHasUnseenCompletion(summary) ? "completed" : null;
  }
  if (summary.hasPendingApprovals) return "approval";
  if (summary.hasPendingUserInput) return "input";
  return "plan";
}

const ATTENTION_KIND_PRIORITY: Record<CrossProjectAttentionKind, number> = {
  approval: 0,
  input: 1,
  plan: 2,
  completed: 3,
};

/**
 * Attention outside the focused project. This deliberately starts from the
 * complete store snapshot: archived rows have already been removed by the
 * sidebar selector, but they still need an explicit exclusion here.
 */
export function computeCrossProjectAttention(input: {
  threads: readonly FocusThread[];
  summariesById: Readonly<Record<string, SidebarThreadSummary | undefined>>;
  projects: readonly Project[];
  focusedProjectId: Thread["projectId"] | null;
  excludeThreadIds?: ReadonlySet<ThreadId>;
  nowMs: number;
}): CrossProjectAttention {
  const projectById = new Map(input.projects.map((project) => [project.id, project] as const));
  const attentionByProjectId = new Map<
    Thread["projectId"],
    Array<{ thread: FocusThread; kind: CrossProjectAttentionKind }>
  >();
  const threadById = new Map(input.threads.map((thread) => [thread.id, thread] as const));

  for (const thread of input.threads) {
    if (
      thread.projectId === input.focusedProjectId ||
      thread.archivedAt != null ||
      (thread.parentThreadId != null &&
        !isInitiativeChildThread(thread, (id) => threadById.get(id))) ||
      input.excludeThreadIds?.has(thread.id) ||
      !projectById.has(thread.projectId)
    ) {
      continue;
    }
    const summary = input.summariesById[thread.id];
    if (!summary || isThreadSnoozed(summary, input.nowMs)) continue;
    const kind = attentionKind(summary);
    if (!kind) continue;

    const projectThreads = attentionByProjectId.get(thread.projectId) ?? [];
    projectThreads.push({ thread, kind });
    attentionByProjectId.set(thread.projectId, projectThreads);
  }

  const byProject = [...attentionByProjectId.entries()]
    .map(([projectId, threads]) => ({
      project: projectById.get(projectId)!,
      threads,
    }))
    .map(({ project, threads }) => ({
      project,
      threads: threads.toSorted((left, right) => {
        const byKind = ATTENTION_KIND_PRIORITY[left.kind] - ATTENTION_KIND_PRIORITY[right.kind];
        return byKind === 0 ? left.thread.id.localeCompare(right.thread.id) : byKind;
      }),
    }))
    .toSorted((left, right) => {
      const leftUrgent = left.threads.some(({ kind }) => kind !== "completed");
      const rightUrgent = right.threads.some(({ kind }) => kind !== "completed");
      if (leftUrgent !== rightUrgent) return leftUrgent ? -1 : 1;
      return left.project.name.localeCompare(right.project.name);
    });
  const total = byProject.reduce((count, { threads }) => count + threads.length, 0);

  return {
    level:
      total === 0
        ? null
        : byProject.some(({ threads }) => threads.some(({ kind }) => kind !== "completed"))
          ? "urgent"
          : "info",
    total,
    byProject,
  };
}
